/**
 * Typed fetch wrapper. Base URL from Vite env. Every request carries the access token as a
 * Bearer header; a 401 triggers a single refresh-then-retry. Tenancy is NEVER sent from here:
 * orgId/role are derived server-side from the JWT, never the request body.
 */
import { getToken, getRefreshToken, getSession, setSession, clearSession } from "./auth.js";

export const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** internal — prevents infinite refresh loops */
  _retried?: boolean;
}

/** Session expired and could not be refreshed: drop it and bounce to /login. */
function onSessionLost(): void {
  clearSession();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

/**
 * Attempt a one-shot token refresh against POST /auth/refresh. Returns true and persists the
 * rotated tokens on success. If there is no refresh token we fail quietly (e.g. the login
 * request itself 401s); if the refresh is rejected we clear the session and redirect.
 */
async function tryRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      onSessionLost();
      return false;
    }
    const data = (await res.json()) as {
      accessToken?: string;
      refreshToken?: string;
      tokenType?: string;
    };
    const current = getSession();
    if (!data.accessToken || !current) {
      onSessionLost();
      return false;
    }
    setSession({
      ...current,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken ?? current.refreshToken,
      tokenType: data.tokenType ?? current.tokenType,
    });
    return true;
  } catch {
    return false;
  }
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 401 && !opts._retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiFetch<T>(path, { ...opts, _retried: true });
  }

  if (!res.ok) {
    let body: unknown = undefined;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    // The server's own reason when it gives one ({ message } — e.g. a 402 "Not enough resumes left:
    // this needs 40, your organisation has 10…"), so screens that show err.message say something a
    // person can act on instead of "Request failed: 402".
    const serverMessage = (body as { message?: unknown } | undefined)?.message;
    throw new ApiError(
      res.status,
      typeof serverMessage === "string" && serverMessage.trim() ? serverMessage.trim().slice(0, 400) : `Request failed: ${res.status}`,
      body,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiFetch<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: "PATCH", body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};

/**
 * A JSON request that reports UPLOAD progress — for CV uploads, where the body is a whole file and
 * a person needs to see it going. Same contract as apiFetch: Bearer token, one refresh-then-retry
 * on 401, ApiError with the server's own message. `onUploaded` fires once the last byte is sent;
 * from then on the server is working (extracting, evaluating) and only the response says how it went.
 */
export function apiUpload<T>(
  method: "POST" | "PATCH" | "PUT",
  path: string,
  body: unknown,
  hooks: { onProgress?: (fraction: number) => void; onUploaded?: () => void } = {},
  retried = false,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${BASE_URL}${path}`);
    xhr.setRequestHeader("Content-Type", "application/json");
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) hooks.onProgress?.(e.loaded / e.total);
    };
    xhr.upload.onload = () => {
      hooks.onProgress?.(1);
      hooks.onUploaded?.();
    };
    xhr.onerror = () => reject(new ApiError(0, "Network error — the upload didn't reach the server. Check your connection and retry."));
    xhr.ontimeout = () => reject(new ApiError(0, "The server took too long to answer. The CV may still be processing — refresh in a minute."));
    xhr.onload = async () => {
      if (xhr.status === 401 && !retried && (await tryRefresh())) {
        apiUpload<T>(method, path, body, hooks, true).then(resolve, reject);
        return;
      }
      let parsed: unknown = undefined;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        /* non-JSON body */
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const msg = (parsed as { message?: unknown } | undefined)?.message;
        reject(new ApiError(xhr.status, typeof msg === "string" && msg.trim() ? msg.trim().slice(0, 400) : `Request failed: ${xhr.status}`, parsed));
        return;
      }
      resolve(parsed as T);
    };
    xhr.send(JSON.stringify(body));
  });
}

/** A File as base64 (no data: prefix) — via FileReader, fast even for a multi-MB PDF. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => reject(r.error ?? new Error("Couldn't read the file"));
    r.readAsDataURL(file);
  });
}
